import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, map, of, tap } from 'rxjs';
import { StoreErrorHandler } from '../base/effects.base';
import { moneyTransactionsActions, moneyTransactionsApiActions } from './money-transactions.actions';
import { MoneyTransactionsInfrastructure } from './money-transactions.infrastructure';
import { moneyTransactionsFeature } from './money-transactions.reducers';

@Injectable({ providedIn: 'root' })
export class MoneyTransactionsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly moneyTransactionsInfrastructure = inject(MoneyTransactionsInfrastructure);
  private readonly storeErrorHandler = inject(StoreErrorHandler);

  public loadMoneyTransactions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(moneyTransactionsActions.loadMoneyTransactions),
      concatLatestFrom(() => this.store.select(moneyTransactionsFeature.selectInitiallyLoaded)),
      filter(([{ force }, initiallyLoaded]) => force || !initiallyLoaded),
      map(() => moneyTransactionsApiActions.loadMoneyTransactionsFromApi()),
    );
  });

  public loadMoneyTransactionsFromApi$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        moneyTransactionsApiActions.loadMoneyTransactionsFromApi,
        moneyTransactionsActions.incrementMoneyTransactionsPage,
        moneyTransactionsActions.resetMoneyTransactions,
      ),
      concatLatestFrom(() => this.store.select(moneyTransactionsFeature.selectPage)),
      concatMap(([, page]) =>
        this.moneyTransactionsInfrastructure.getMoneyAccountTransactions(15, page).pipe(
          map(({ list, hasNext }) => moneyTransactionsApiActions.loadMoneyTransactionsSuccess({ moneyTransactions: list, hasNext })),
          catchError((error) => of(moneyTransactionsApiActions.loadMoneyTransactionsFailure({ error }))),
        ),
      ),
    );
  });

  public handleBackendErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(moneyTransactionsApiActions.loadMoneyTransactionsFailure),
        tap(({ error }) => this.storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
